import { ConflictError } from '@/api/ConflictError'
import {
    MembershipRequestType,
    membershipRequestSchema,
} from '@/api/membershipRequest'
import {
    membershipRequestQueryOptions,
    useApproveAnonymousMembershipRequestMutation,
    useDenyAnonymousMembershipRequestMutation,
} from '@/api/membershipRequestQueryOptions'
import FormDebug from '@/components/FormDebug'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid, TextField } from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'

export const Route = createFileRoute(
    '/approveMembershipRequest_/$membershipRequestCode'
)({
    component: ApproveMembershipRequestComponent,
})

function ApproveMembershipRequestComponent() {
    const params = Route.useParams()
    const navigate = useNavigate()

    const membershipRequestQuery = useSuspenseQuery(
        membershipRequestQueryOptions(params.membershipRequestCode),
    )
    const membershipRequest = membershipRequestQuery.data

    const onSuccess = (updatedMembershipRequest: MembershipRequestType) => {
        updatedMembershipRequest
        enqueueSnackbar('Erfolgreich', { variant: 'success' })
        navigate({
            to: '/',
        })
    }

    const onError = (error: any) => {
        error
        if (error instanceof ConflictError) {
            enqueueSnackbar(t('error.membershipRequest.alreadyFinished'), {
                variant: 'error',
            })
        } else {
            enqueueSnackbar('Fehler beim Verarbeiten der Mitgliedschaftsanfrage', {
                variant: 'error',
            })
        }
    }

    const approveMembershipRequest = useApproveAnonymousMembershipRequestMutation(
        onSuccess,
        onError,
    )
    const denyMembershipRequest = useDenyAnonymousMembershipRequestMutation(
        onSuccess,
        onError,
    )

    const {
        control,
        register,
        getValues,
        formState: { isDirty, isValid, errors },
    } = useForm<MembershipRequestType>({
        resolver: yupResolver<MembershipRequestType, any, any>(membershipRequestSchema),
        mode: 'onChange',
        shouldFocusError: true,
    })
    isValid
    errors

    const approve = (data: MembershipRequestType) => {
        console.log('approve')
        data.replyMessage = getValues('replyMessage')
        approveMembershipRequest.mutate(data)
    }

    const deny = (data: MembershipRequestType) => {
        console.log('deny')
        data.replyMessage = getValues('replyMessage')
        denyMembershipRequest.mutate(data)
    }

    return (
        <>
            <h1>
                {t('entity.membershipRequest')} {t('property.membershipRequest.for')}{' '}
                {membershipRequest.member?.firstname}{' '}
                {membershipRequest.member?.lastname}
            </h1>
            <form>
                <Grid container spacing={1}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 8
                        }}>
                        <div>
                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 6
                                    }}>
                                    <input
                                        type="hidden"
                                        {...register('uuid')}
                                        defaultValue={membershipRequest.uuid}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('code')}
                                        defaultValue={membershipRequest.code}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('member.uuid')}
                                        defaultValue={membershipRequest.member?.uuid}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('mainOrganization.uuid')}
                                        defaultValue={membershipRequest.mainOrganization?.uuid}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('requestingOrganization.uuid')}
                                        defaultValue={
                                            membershipRequest.requestingOrganization?.uuid
                                        }
                                    />
                                    <input
                                        type="hidden"
                                        {...register('requestingUser.uuid')}
                                        defaultValue={membershipRequest.requestingUser?.uuid}
                                    />
                                    <TextField
                                        {...register('member.scoutId')}
                                        defaultValue={membershipRequest.member?.scoutId}
                                        label="Scout-ID"
                                        slotProps={{
                                            htmlInput: { readOnly: true }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 6
                                    }}>
                                    <TextField
                                        {...register('member.lastname')}
                                        defaultValue={membershipRequest.member?.lastname}
                                        label={t('property.member.lastname')}
                                        slotProps={{
                                            htmlInput: { readOnly: true }
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 6
                                    }}>
                                    <TextField
                                        {...register('member.firstname')}
                                        defaultValue={membershipRequest.member?.firstname}
                                        label={t('property.member.firstname')}
                                        slotProps={{
                                            htmlInput: { readOnly: true }
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 6
                                    }}>
                                    <ScoregDatePicker
                                        name="member.birthdate"
                                        defaultValue={
                                            membershipRequest.member?.birthdate
                                                ? dayjs(membershipRequest.member.birthdate)
                                                : undefined
                                        }
                                        disabled={true}
                                        control={control}
                                        label={t('property.member.birthdate')}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
                <Grid container spacing={1}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 6
                        }}>
                        <TextField
                            {...register('message')}
                            defaultValue={membershipRequest.message}
                            label={t('property.membershipRequest.message')}
                            slotProps={{
                                htmlInput: { readOnly: true }
                            }}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 6
                        }}>
                        <TextField
                            {...register('replyMessage')}
                            label={t('property.membershipRequest.replyMessage')}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <Button
                            onClick={() => {
                                approve(membershipRequest)
                            }}
                        >
                            {t('action.approve')}
                        </Button>
                        <Button
                            onClick={() => {
                                deny(membershipRequest)
                            }}
                            color="error"
                        >
                            {t('action.deny')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <FormDebug
                errors={errors}
                dirty={isDirty}
                valid={isValid}
                value={membershipRequest}
            />
            {import.meta.env.VITE_DEBUG === 'true' &&
                <DevTool control={control} />
            }
        </>
    );
}
